import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Alert, Button } from './primitives';

type Detector = { detect: (src: CanvasImageSource) => Promise<{ rawValue: string }[]> };
declare global {
  interface Window {
    BarcodeDetector?: new (opts: { formats: string[] }) => Detector;
  }
}

/**
 * Leitor de QR pela câmera. Usa o BarcodeDetector do navegador quando existe (Chrome/Android) e jsQR nos demais.
 * A câmera só liga depois do clique e é desligada ao fechar. Se não houver câmera/permissão, o código pode ser digitado.
 */
export function QrScanner({ onResult, onClose }: { onResult: (text: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    const detector = window.BarcodeDetector ? new window.BarcodeDetector({ formats: ['qr_code'] }) : null;

    const tick = async () => {
      if (stopped) return;
      const v = video.current;
      const c = canvas.current;
      if (v && c && v.readyState >= 2) {
        try {
          let text: string | null = null;
          if (detector) {
            text = (await detector.detect(v))[0]?.rawValue ?? null;
          } else {
            c.width = v.videoWidth;
            c.height = v.videoHeight;
            const ctx = c.getContext('2d', { willReadFrequently: true })!;
            ctx.drawImage(v, 0, 0);
            const img = ctx.getImageData(0, 0, c.width, c.height);
            text = jsQR(img.data, img.width, img.height)?.data ?? null;
          }
          if (text) {
            stopped = true;
            onResult(text);
            return;
          }
        } catch {
          /* quadro ilegível: tenta o próximo */
        }
      }
      raf = requestAnimationFrame(() => void tick());
    };

    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((s) => {
        stream = s;
        if (video.current) {
          video.current.srcObject = s;
          void video.current.play();
        }
        raf = requestAnimationFrame(() => void tick());
      })
      .catch(() => setError('Não foi possível usar a câmera. Permita o acesso ou digite o código.'));
    if (!navigator.mediaDevices) setError('Este navegador não dá acesso à câmera. Digite o código.');

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onResult]);

  return (
    <div className="space-y-3">
      {error ? (
        <Alert tone="warning">{error}</Alert>
      ) : (
        <div className="relative overflow-hidden rounded-2xl bg-ink">
          <video ref={video} className="aspect-square w-full object-cover" muted playsInline />
          <div className="pointer-events-none absolute inset-8 rounded-2xl border-4 border-white/80" aria-hidden />
        </div>
      )}
      <canvas ref={canvas} className="hidden" />
      <Button variant="secondary" className="w-full" onClick={onClose}>Fechar câmera</Button>
    </div>
  );
}
